import React, { forwardRef, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import QueryParamLink from 'components/atoms/QueryParamLink';
import Pagination from 'components/molecules/Pagination';
import CardLoading from 'components/molecules/CardLoading';

import styles from './CardList.module.scss';

interface Props {
  cardRenderer: (dataItem: Record<string, any>) => React.ReactNode;
  currentPage: number;
  data: Array<Record<string, any>>;
  loadingPlaceholderHeight?: string;
  pageQueryParamName: string;
  pageSize: number;
  testId?: string;
  title?: string;
  totalItems: number;
}

const CardList = forwardRef<HTMLDivElement, Props>(
  (
    {
      currentPage,
      data,
      loadingPlaceholderHeight = '550px',
      pageQueryParamName = '',
      pageSize,
      testId = '',
      title = '',
      totalItems = 0,
      cardRenderer,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const canLoadMore = pageSize * currentPage < totalItems;
    const itemsSortedByPage = useMemo(() => _.sortBy(data, 'page'), [data]);

    return (
      <div ref={ref} className={styles.container} data-test-id={testId}>
        <h2 className={styles.title}>{title}</h2>
        {currentPage > 1 && (
          <h2 className={styles.title}>{`(${t('pageNumber', { number: currentPage })})`}</h2>
        )}

        <div
          className={classNames(styles.cards, {
            [styles['cards-vertical']]: currentPage > 1,
          })}
        >
          {itemsSortedByPage.length
            ? itemsSortedByPage
                .map((dataItem, index) => (
                  <article
                    className={classNames(styles['card-container'], {
                      [styles['card-container-hidden']]: dataItem.page === 1 && currentPage > 1,
                      [styles['card-container-hide-md']]: dataItem.page !== currentPage,
                    })}
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                  >
                    {cardRenderer(dataItem)}
                  </article>
                ))
                .concat(
                  Array.from(
                    {
                      length: itemsSortedByPage.some((dataItem) => dataItem.page === currentPage)
                        ? 0
                        : pageSize,
                    },
                    (_value, index) => (
                      <article className={styles['card-container']} key={`${currentPage}-${index}`}>
                        <CardLoading
                          style={{ height: loadingPlaceholderHeight }}
                          testId={`${testId}-card`}
                        />
                      </article>
                    )
                  )
                )
            : canLoadMore &&
              Array.from({ length: pageSize }, (_value, i) => (
                <article
                  className={styles['card-container']}
                  key={`${testId}-card-loading${i + 1}`}
                >
                  <CardLoading
                    style={{ height: loadingPlaceholderHeight }}
                    testId={`${testId}-card`}
                  />
                </article>
              ))}
          {currentPage === 1 && canLoadMore && (
            <QueryParamLink params={{ [pageQueryParamName]: '2' }}>
              <a
                className={styles['first-page-load-more-mobile']}
                data-test-id={`${testId}-load-more-page-1`}
                rel="next"
              >
                <svg
                  enableBackground="new 0 0 24 24"
                  version="1.1"
                  viewBox="0 0 24 24"
                  xmlSpace="preserve"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <polygon
                    points="12 4.3 10.6 5.7 15.9 11 4.3 11 4.3 13 15.9 13 10.6 18.3 12 19.7 19.7 12"
                    fill="#0686f9"
                  />
                </svg>
                {t('loadMore')}
              </a>
            </QueryParamLink>
          )}
        </div>
        {currentPage > 1 && canLoadMore && (
          <QueryParamLink params={{ [pageQueryParamName]: (currentPage + 1).toString() }}>
            <a
              rel="next"
              className={styles['load-more-mobile']}
              data-test-id={`${testId}-show-more`}
            >
              {t('loadMore')}
            </a>
          </QueryParamLink>
        )}
        {pageSize < totalItems && (
          <nav className={styles.pagination}>
            <Pagination
              currentPage={currentPage}
              itemsPerPage={pageSize}
              totalItems={totalItems}
              pageQueryParamName={pageQueryParamName}
              testId={testId}
            />
          </nav>
        )}
      </div>
    );
  }
);
CardList.displayName = 'CardList';

export type { Props as CardListProps };
export default CardList;
